import { useCallback, useState, useMemo } from "react"
import { BaseFilters, SortConfig, UsePaginatedQueryReturn } from "../types/pagination"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"
import { useQueryWithEndpoint } from "./useGraphQLClient"
import { DEFAULT_PAGE_SIZE } from "@hydrologiq/shared/config"

export interface SystemFilters extends BaseFilters {
  name?: string
  customer?: string
  site?: string
}

export interface SystemFilterOptions {
  customers: Array<{ name: string; value: string }>
  sites: Array<{ name: string; value: string }>
}

export function usePaginatedSystems(
  customerId?: string,
  siteId?: string,
  initialPageSize: number = DEFAULT_PAGE_SIZE
): UsePaginatedQueryReturn<gqlTypes.SystemListItem, SystemFilters> {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [filters, setFiltersState] = useState<SystemFilters>({})
  const [sort, setSortState] = useState<SortConfig | null>({ field: "name", direction: "asc" })

  const variables = useMemo(() => {
    const vars: Record<string, any> = {
      page: currentPage,
      perPage: pageSize,
    }

    // Props take priority for customer/site filtering (embedded table use case)
    // If filter is set by user interaction, it overrides the prop
    vars.customerId = filters.customer || customerId || undefined
    vars.siteId = filters.site || siteId || undefined

    if (filters.name) vars.name = filters.name
    if (sort?.field && sort?.direction) {
      vars.sort = `${sort.field}:${sort.direction}`
    }

    return vars
  }, [currentPage, pageSize, filters, sort, customerId, siteId])

  const { data, loading, error, refetch } = useQueryWithEndpoint(
    gqlTypes.GetSystemsListDocument,
    { variables, fetchPolicy: "network-only", notifyOnNetworkStatusChange: true },
    "primary"
  )

  const systems = data?.systemsList?.data || []
  const pagination = data?.systemsList?.meta || null
  const filterOptions = (data?.systemsList as any)?.filterOptions || null

  const fetchPage = useCallback(
    async (page?: number, newPageSize?: number) => {
      if (page !== undefined) setCurrentPage(page)
      if (newPageSize !== undefined) setPageSize(newPageSize)
    },
    []
  )

  const setFilters = useCallback((newFilters: SystemFilters) => {
    setFiltersState(newFilters)
    setCurrentPage(1)
  }, [])

  const setSort = useCallback((newSort: SortConfig | null) => {
    setSortState(newSort)
    setCurrentPage(1)
  }, [])

  const clearFilters = useCallback(() => {
    setFiltersState({})
    setCurrentPage(1)
  }, [])

  const refetchWrapper = useCallback(async () => {
    await refetch()
  }, [refetch])

  return {
    data: systems,
    pagination,
    filterOptions,
    loading: loading || false,
    error: error?.message || null,
    filters,
    sort,
    fetchPage,
    setFilters,
    setSort,
    clearFilters,
    refetch: refetchWrapper,
  }
}
